/**
 * Newsletter Provider Component
 * Manages global newsletter state and provides context to children
 */

import { createContext, useCallback, useState, type ReactNode } from 'react';
import type { NewsletterCategory, NewsletterContextValue, NewsletterFormData, NewsletterState, WizardStep } from '../types';
import { DEFAULT_FORM_DATA } from '../constants';
import { subscribeToNewsletter } from '../api';
import { NewsletterModal } from './NewsletterModal';

export const NewsletterContext = createContext<NewsletterContextValue | null>(null);

interface NewsletterProviderProps {
  children: ReactNode;
}

const initialState: NewsletterState = {
  isModalOpen: false,
  currentStep: 1,
  formData: DEFAULT_FORM_DATA,
  isSubmitting: false,
  error: null,
  alreadySubscribed: false,
};

export function NewsletterProvider({ children }: NewsletterProviderProps) {
  const [state, setState] = useState<NewsletterState>(initialState);

  const openModal = useCallback((prefillEmail?: string, prefillCategories?: NewsletterCategory[]) => {
    setState((prev) => ({
      ...prev,
      isModalOpen: true,
      currentStep: 1,
      formData: {
        ...DEFAULT_FORM_DATA,
        ...(prefillEmail ? { email: prefillEmail } : {}),
        ...(prefillCategories?.length ? { categories: prefillCategories } : {}),
      },
      error: null,
      alreadySubscribed: false,
    }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isModalOpen: false,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 3) as WizardStep,
      error: null,
    }));
  }, []);

  const prevStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1) as WizardStep,
      error: null,
    }));
  }, []);

  const updateFormData = useCallback((data: Partial<NewsletterFormData>) => {
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, ...data },
      error: null,
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const submit = useCallback(async () => {
    setState((prev) => ({ ...prev, isSubmitting: true, error: null }));

    try {
      const response = await subscribeToNewsletter({
        email: state.formData.email,
        firstName: state.formData.firstName,
        lastName: state.formData.lastName,
        categories: state.formData.categories,
        termsAccepted: state.formData.termsAccepted,
      });

      if (response.error) {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          error: response.error || null,
        }));
        return;
      }

      if (response.alreadySubscribed) {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          alreadySubscribed: true,
        }));
        return;
      }

      if (response.alreadyPending) {
        setState((prev) => ({
          ...prev,
          isSubmitting: false,
          currentStep: 3,
        }));
        return;
      }

      // Success - go to step 3
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        currentStep: 3,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: 'Κάτι πήγε στραβά. Παρακαλούμε δοκιμάστε ξανά.',
      }));
    }
  }, [state.formData]);

  const contextValue: NewsletterContextValue = {
    ...state,
    openModal,
    closeModal,
    nextStep,
    prevStep,
    updateFormData,
    submit,
    reset,
  };

  return (
    <NewsletterContext.Provider value={contextValue}>
      {children}
      <NewsletterModal />
    </NewsletterContext.Provider>
  );
}
